import React, { useState, useEffect, useCallback } from 'react';
import turfDistance from '@turf/distance';
import { toFeatureCollection } from '../lib/gis-types';
import { addAnnotation, updateAnnotation, deleteAnnotation, findAnnotationAt } from '../lib/annotations';

/** Measure + annotate tool state for MapView. */
export function useMapTools(tool, { onNearestClick } = {}) {
  const [annotations, setAnnotations] = useState([]);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [measureDone, setMeasureDone] = useState(false);
  // Inline annotation editor: { id | null, text, lon, lat } (id null = new annotation)
  const [annotationEdit, setAnnotationEdit] = useState(null);

  // Clear measurement when leaving the measure tool
  useEffect(() => { if (tool !== 'annotate') setAnnotationEdit(null); }, [tool]);

  useEffect(() => {
    if (tool !== 'measure') {
      setMeasurePoints([]);
      setMeasureDone(false);
    }
  }, [tool]);

  const resetMeasure = useCallback(() => {
    setMeasurePoints([]);
    setMeasureDone(false);
  }, []);

  // Returns true when the click was consumed by the measure / annotate tool.
  const handleToolClick = useCallback((evt) => {
    const clickCoord = [evt.lngLat.lng, evt.lngLat.lat];

    // Measure tool — click to add vertices, dblclick to finish
    if (tool === 'measure') {
      if (measureDone) {
        // Start a fresh measurement
        setMeasurePoints([clickCoord]);
        setMeasureDone(false);
      } else {
        setMeasurePoints(prev => [...prev, clickCoord]);
      }
      return true;
    }

    // Find nearest — the click point is the search origin (hooks/useNearestTool.js)
    if (tool === 'nearest') {
      if (onNearestClick) onNearestClick(evt);
      return true;
    }

    // Annotate tool — place a text pin
    if (tool === 'annotate') {
      const map = evt.target;
      const hit = findAnnotationAt(annotations, evt.point, map?.project ? (c) => map.project(c) : null);
      setAnnotationEdit(hit
        ? { id: hit.id, text: hit.text, lon: hit.lon, lat: hit.lat }
        : { id: null, text: '', lon: evt.lngLat.lng, lat: evt.lngLat.lat });
      return true;
    }
    return false;
  }, [tool, measureDone, onNearestClick, annotations]);

  const saveAnnotation = useCallback((text) => {
    setAnnotationEdit((edit) => {
      if (!edit) return null;
      setAnnotations((prev) => (edit.id
        ? updateAnnotation(prev, edit.id, text)
        : addAnnotation(prev, { text, lon: edit.lon, lat: edit.lat })));
      return null;
    });
  }, []);
  const removeAnnotation = useCallback(() => {
    setAnnotationEdit((edit) => {
      if (edit?.id) setAnnotations((prev) => deleteAnnotation(prev, edit.id));
      return null;
    });
  }, []);
  const cancelAnnotationEdit = useCallback(() => setAnnotationEdit(null), []);

  // Finish measurement on double-click (drawing finish is handled by Terra Draw)
  const handleMapDblClick = useCallback((evt) => {
    if (tool === 'measure' && measurePoints.length >= 2) {
      setMeasureDone(true);
    }
  }, [tool, measurePoints]);

  const annotationData = React.useMemo(() => toFeatureCollection(annotations.map(a => ({
    type: 'Feature',
    properties: { id: a.id, text: a.text },
    geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
  }))), [annotations]);

  // Measurement — total distance along clicked vertices
  const measureTotalKm = measurePoints.length >= 2
    ? measurePoints.slice(1).reduce((sum, pt, i) => sum + turfDistance(measurePoints[i], pt, { units: 'kilometers' }), 0)
    : 0;
  const measureGeoJSON = React.useMemo(() => ({
    type: 'FeatureCollection',
    features: [
      ...(measurePoints.length >= 2 ? [{
        type: 'Feature', properties: {},
        geometry: { type: 'LineString', coordinates: measurePoints },
      }] : []),
      ...measurePoints.map((c, i) => ({
        type: 'Feature', properties: { index: i },
        geometry: { type: 'Point', coordinates: c },
      })),
    ],
  }), [measurePoints]);

  return {
    annotations, annotationData, measurePoints, measureDone, measureTotalKm, measureGeoJSON,
    annotationEdit, saveAnnotation, removeAnnotation, cancelAnnotationEdit,
    resetMeasure, handleToolClick, handleMapDblClick,
  };
}
